import { PlainButton, Tooltip } from "@jugl-web/ui-components";
import {
  InteractiveContainer,
  MultiSectionLayout,
} from "@jugl-web/ui-components/cross-platform";
import {
  ResizableSidebarWrapper,
  useResizableSidebar,
} from "@web-src/components/ResizableSidebar";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useTheme } from "@web-src/themes/useTheme";
import { cx, Lottie, useTranslations } from "@jugl-web/utils";
import { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { isAppInDevMode } from "@web-src/modules/common/utils/isAppInDevMode";
import { CXCreditsTile } from "@web-src/modules/cx/components/CXCreditsTile";
import { OnboardingFlowTooltip } from "@web-src/modules/cx/components/OnboardingFlowTooltip";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { useCRMUnreadIndicators } from "@web-src/modules/client-support/providers/CRMUnreadIndicatorsProvider";
import { ReactComponent as InboxIcon } from "./assets/inbox.svg";
import { ReactComponent as AIAgentsIcon } from "./assets/ai-agents.svg";
import { ReactComponent as AnalyticsIcon } from "./assets/analytics.svg";
import { ReactComponent as ContactsIcon } from "./assets/contacts.svg";
import { ReactComponent as ChevronRightIcon } from "./assets/chevron-right.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import errorAnimation from "./assets/error-animation.json";

const CX_SIDEBAR_EXPANDED_KEY = "JUGL__CX_SIDEBAR_EXPANDED";
const CX_SIDEBAR_MANUAL_WIDTH = "JUGL__CX_SIDEBAR_MANUAL_WIDTH";
const CX_SIDEBAR_DEFAULT_EXPANDED_WIDTH = 240;
const CX_SIDEBAR_COLLAPSE_THRESHOLD = 160;

export const CXHomeSidebar: FC = () => {
  const { t } = useTranslations();
  const { tcx } = useTheme();
  const { navigateToPage } = useNavigation();
  const navigate = useNavigate();
  const { entityId } = useEntitySelectedProvider();
  const { isAdmin } = useUserRole();
  const { totalUnreadCount: crmUnreadCount } = useCRMUnreadIndicators();

  const { isSidebarExpanded, sidebarWidth, minWidth, updateSidebarWidth } =
    useResizableSidebar({
      expandedStorageKey: CX_SIDEBAR_EXPANDED_KEY,
      widthStorageKey: CX_SIDEBAR_MANUAL_WIDTH,
      defaultExpandedWidth: CX_SIDEBAR_DEFAULT_EXPANDED_WIDTH,
      collapseThreshold: CX_SIDEBAR_COLLAPSE_THRESHOLD,
    });

  return (
    <MultiSectionLayout>
      <ResizableSidebarWrapper
        sidebarWidth={sidebarWidth}
        onWidthChange={updateSidebarWidth}
      >
        {(isResizing) => (
          <aside
            className={tcx(
              "group/cxHomeSideBar relative flex h-full flex-col border-0 border-r-[1px] border-solid border-[#D6D6DE] transition-[width] duration-300",
              {
                light: "border-primary-500 bg-primary-600",
                dark: "bg-[#212121]",
                blue: "border-primary-700 bg-primary-800",
              },
              isResizing && "transition-none"
            )}
            style={{ width: `${sidebarWidth}px` }}
          >
            <div className="relative flex shrink-0 flex-col">
              {!isResizing && (
                <InteractiveContainer
                  className={tcx(
                    "fixed top-[48px] z-[49] flex h-6 w-6 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-solid border-white bg-primary-600 text-white opacity-0 transition-all duration-300 group-hover/cxHomeSideBar:opacity-100",
                    isSidebarExpanded && "-rotate-180",
                    {
                      blue: "bg-primary-600",
                      dark: "bg-[#3E3F40]",
                      light: "bg-[#42A4F5]",
                    }
                  )}
                  onClick={() => {
                    const newWidth = isSidebarExpanded
                      ? minWidth
                      : CX_SIDEBAR_DEFAULT_EXPANDED_WIDTH;
                    updateSidebarWidth(newWidth);
                  }}
                  style={{ left: `${sidebarWidth - 24}px` }}
                >
                  <ChevronRightIcon />
                </InteractiveContainer>
              )}
            </div>
            <div className="flex flex-1 flex-col items-center gap-3 overflow-y-auto py-2.5">
              <div className="flex w-full flex-col items-center gap-3 px-2.5">
                <NavItem
                  label={t({
                    id: "cx-home-sidebar.inbox",
                    defaultMessage: "Inbox",
                  })}
                  icon={<InboxIcon />}
                  onClick={() => navigateToPage("clientSupport")}
                  pathSegment="client-support"
                  isExpanded={isSidebarExpanded}
                  value={crmUnreadCount}
                />
              </div>

              <div
                className={tcx("h-px w-full shrink-0", {
                  light: "bg-primary-500",
                  dark: "bg-[#2A2A2A]",
                  blue: "bg-primary-700",
                })}
              />

              <div className="flex w-full flex-col items-center gap-3 px-2.5">
                <NavItem
                  label={t({
                    id: "cx-home-sidebar.ai-agents",
                    defaultMessage: "AI Agents",
                  })}
                  icon={<AIAgentsIcon />}
                  onClick={() => navigateToPage("cxAIAgents")}
                  pathSegment="cx/ai-agents"
                  isExpanded={isSidebarExpanded}
                />
                <NavItem
                  label={t({
                    id: "cx-home-sidebar.analytics",
                    defaultMessage: "Analytics",
                  })}
                  icon={<AnalyticsIcon />}
                  onClick={() => navigateToPage("cxAnalytics")}
                  pathSegment="cx/analytics"
                  isExpanded={isSidebarExpanded}
                />
                <NavItem
                  label={t({
                    id: "cx-home-sidebar.customers",
                    defaultMessage: "Customers",
                  })}
                  icon={<ContactsIcon />}
                  onClick={() => navigateToPage("contactsContacts")}
                  pathSegment="contacts"
                  isExpanded={isSidebarExpanded}
                />
                <NavItem
                  label={t({
                    id: "cx-home-sidebar.settings",
                    defaultMessage: "Settings",
                  })}
                  icon={<SettingsIcon />}
                  onClick={() =>
                    isAdmin
                      ? navigateToPage("cxWorkspaceGeneral")
                      : navigateToPage("cxWorkspaceMyAccount")
                  }
                  pathSegment="settings"
                  isExpanded={isSidebarExpanded}
                />
                {isAppInDevMode() && (
                  <>
                    <NavItem
                      label={t({
                        id: "cx-home-sidebar.agent-settup",
                        defaultMessage: "Agent Setup",
                      })}
                      icon={<span className="text-lg">⚙️</span>}
                      onClick={() => navigateToPage("cxActionsForm")}
                      pathSegment="cx/actions/form"
                      isExpanded={isSidebarExpanded}
                    />
                    <NavItem
                      label="Debug Sub V2"
                      icon={<span className="text-lg">⚙️</span>}
                      onClick={() =>
                        navigate(`/${entityId}/debug/subscription-v2`)
                      }
                      pathSegment="debug/subscription-v2"
                      isExpanded={isSidebarExpanded}
                    />
                  </>
                )}
              </div>
            </div>
            <SubscriptionCanceledBanner isExpanded={isSidebarExpanded} />
            <PaymentFailedBanner isExpanded={isSidebarExpanded} />
            <OnboardingFlowTooltip
              flow="postPurchase"
              step={0}
              placement="right"
            >
              <CXCreditsTile isExpanded={isSidebarExpanded} />
            </OnboardingFlowTooltip>
          </aside>
        )}
      </ResizableSidebarWrapper>
    </MultiSectionLayout>
  );
};

const SidebarBanner: FC<{
  isExpanded?: boolean;
  title: string;
  description: React.ReactNode;
  collapsedTitle: string;
  onClick: () => void;
}> = ({ isExpanded = true, title, description, collapsedTitle, onClick }) => {
  const { tcx } = useTheme();

  if (!isExpanded) {
    return (
      <PlainButton
        className={tcx(
          "m-3 mx-auto flex h-10 w-10 items-center justify-center rounded-xl",
          {
            light: "bg-[#FFF3E0]",
            dark: "bg-[#2A2A2A]",
            blue: "bg-primary-900",
          }
        )}
        onClick={onClick}
        title={collapsedTitle}
      >
        <Lottie
          options={{ animationData: errorAnimation }}
          className="shrink-0"
          height={18}
          width={18}
        />
      </PlainButton>
    );
  }

  return (
    <div
      className={tcx("m-2.5 space-y-3 rounded-xl px-2.5 py-4", {
        light: "bg-[#FFF3E0]",
        dark: "bg-[#2A2A2A]",
        blue: "bg-primary-900",
      })}
    >
      <div className="flex items-center gap-2">
        <Lottie
          options={{ animationData: errorAnimation }}
          className="shrink-0"
          height={18}
          width={18}
        />
        <span
          className={tcx("font-secondary text-sm font-semibold", {
            light: "text-dark",
            dark: "text-white",
            blue: "text-white",
          })}
        >
          {title}
        </span>
      </div>
      <p
        className={tcx("font-secondary text-xs leading-relaxed", {
          light: "text-dark-700",
          dark: "text-[#E0E0E0]",
          blue: "text-grey-300",
        })}
      >
        {description}
      </p>
    </div>
  );
};

const PaymentFailedBanner: FC<{ isExpanded?: boolean }> = ({
  isExpanded = true,
}) => {
  const { t } = useTranslations();
  const { tcx } = useTheme();
  const { isAdmin } = useUserRole();
  const { pendingInvoices } = useSubscriptionV2();
  const { navigateToPage } = useNavigation();

  if (!isAdmin || pendingInvoices.length === 0) return null;

  return (
    <SidebarBanner
      isExpanded={isExpanded}
      collapsedTitle="Payment Failed"
      onClick={() => navigateToPage("cxWorkspacePlans")}
      title={t({
        id: "cx-sidebar.payment-failed",
        defaultMessage: "Payment Failed",
      })}
      description={t(
        {
          id: "cx-sidebar.payment-failed-generic",
          defaultMessage:
            "Please update your payment details and try again. {managePayment}",
        },
        {
          managePayment: (
            <PlainButton
              className={tcx("inline text-xs underline", {
                light: "text-dark-700",
                dark: "text-[#E0E0E0]",
                blue: "text-grey-300",
              })}
              onClick={() => navigateToPage("cxWorkspacePlans")}
            >
              {t({
                id: "cx-sidebar.manage-payment",
                defaultMessage: "Manage Payment",
              })}
            </PlainButton>
          ),
        }
      )}
    />
  );
};

const SubscriptionCanceledBanner: FC<{ isExpanded?: boolean }> = ({
  isExpanded = true,
}) => {
  const { t } = useTranslations();
  const { tcx } = useTheme();
  const { isAdmin } = useUserRole();
  const { isSubscriptionCanceled, currentPlan } = useSubscriptionV2();
  const { navigateToPage } = useNavigation();

  if (!isAdmin || !isSubscriptionCanceled) return null;

  return (
    <SidebarBanner
      isExpanded={isExpanded}
      collapsedTitle="Subscription Canceled"
      onClick={() => navigateToPage("cxWorkspacePlans")}
      title={t({
        id: "cx-sidebar.subscription-canceled",
        defaultMessage: "Subscription Canceled",
      })}
      description={t(
        {
          id: "cx-sidebar.subscription-canceled-description",
          defaultMessage:
            "{planName} Plan subscription is canceled. All Plan features is lost. {manageSubscription}",
        },
        {
          planName: currentPlan?.name ?? "",
          manageSubscription: (
            <span
              className={tcx("inline cursor-pointer text-xs underline", {
                light: "text-dark-700",
                dark: "text-[#E0E0E0]",
                blue: "text-grey-300",
              })}
              onClick={() => navigateToPage("cxWorkspacePlans")}
            >
              {t({
                id: "cx-sidebar.manage-subscription",
                defaultMessage: "Manage Subscription",
              })}
            </span>
          ),
        }
      )}
    />
  );
};

const CX_SIDEBAR_MAX_COUNT = 99;

const CXSidebarCount: FC<{ count: number; isExpanded: boolean }> = ({
  count,
  isExpanded,
}) => {
  const label =
    count > CX_SIDEBAR_MAX_COUNT ? `${CX_SIDEBAR_MAX_COUNT}+` : count;

  return (
    <div
      className={cx(
        "flex items-center justify-center rounded-2xl bg-[#F2F2F4] font-secondary font-semibold text-[#1A2023]",
        isExpanded
          ? "relative h-[22px] min-w-[22px] px-1.5 py-0.5 text-[11px]"
          : "absolute top-0 right-0 h-[18px] min-w-[18px] border-2 border-solid border-[rgba(0,0,0,0.2)] px-1 py-0.5 text-[10px] leading-[1.4]"
      )}
    >
      {label}
    </div>
  );
};

interface NavItemProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  pathSegment: string;
  isExpanded: boolean;
  value?: number;
}
const NavItem: FC<NavItemProps> = ({
  label,
  icon,
  onClick,
  pathSegment,
  isExpanded,
  value,
}) => {
  const { pathname } = useLocation();
  const { tcx } = useTheme();
  const isActive = pathname.includes(pathSegment);

  return (
    <Tooltip
      isDisabled={isExpanded}
      placement="right"
      renderTrigger={({ props, ref }) => (
        <PlainButton
          onClick={onClick}
          className={tcx(
            "relative flex h-10 w-[60px] items-center justify-center gap-2 rounded-lg p-2 text-left font-secondary text-sm text-grey-200 transition-colors",
            isExpanded && "w-full justify-between",
            {
              light: [
                "hover:bg-primary-500",
                isActive ? "bg-primary-500" : "bg-transparent",
              ],
              dark: [
                "hover:bg-[#333333]",
                isActive ? "bg-[#333333]" : "bg-transparent",
              ],
              blue: [
                "hover:bg-primary-700",
                isActive ? "bg-primary-700" : "bg-transparent",
              ],
            }
          )}
          ref={ref}
          {...props}
        >
          <div
            className={cx(
              "flex w-full items-center justify-center gap-1.5",
              value ? "w-3/4" : "w-full"
            )}
          >
            <span className="shrink-0">{icon}</span>
            {isExpanded && (
              <div className="block w-2/3 flex-1 truncate leading-3">
                {label}
              </div>
            )}
          </div>
          {typeof value === "number" && value > 0 && (
            <CXSidebarCount count={value} isExpanded={isExpanded} />
          )}
        </PlainButton>
      )}
    >
      {label}
    </Tooltip>
  );
};
